/* eslint-disable @typescript-eslint/no-non-null-assertion */
import {Component, OnInit} from '@angular/core';
import {LeadSourceResource, LeadSourceService} from '../../../core/services/lead-source.service';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  Validators
} from '@angular/forms';
import {Observable} from 'rxjs';
import {catchError, finalize, map, tap} from 'rxjs/operators';
import {LeadEndpointService, ProductFieldResource} from '../../../core/services/lead-endpoint.service';
import {ProductFormResource, ProductFormsService} from '../../../core/services/product-forms.service';
import {EXAMPLE_LEAD} from './example-lead';
import {ToastrService} from 'ngx-toastr';

interface CreateLeadForm {
  leadSource: FormControl<string>;
  productForm: FormControl<string | null>;
  productFields: FormArray<FormControl<string | null>>;
  coApplicantFields: FormArray<FormGroup<CoApplicantForm>>;
}

interface CoApplicantForm {
  coApplicant: FormArray<FormControl<string | null>>;
}

@Component({
  selector: 'app-lead-creation-form',
  templateUrl: './lead-creation-form.component.html',
  styleUrls: ['./lead-creation-form.component.scss']
})
export class LeadCreationFormComponent implements OnInit {
  createLeadFormGroup = new FormGroup<CreateLeadForm>({
    leadSource: new FormControl('', {nonNullable: true}),
    productForm: new FormControl({value: '', disabled: true}),
    productFields: new FormArray<FormControl<string | null>>([]),
    coApplicantFields: new FormArray<FormGroup<CoApplicantForm>>([]),
  });

  isCreatingLead = false;

  leadSources: Observable<LeadSourceResource[]> = this.leadSourceService.getLeadSources()
    .pipe(
      catchError(() => {
        console.error('❌ Lead-Sources konnten nicht ermittelt werden');
        return [];
      }),
    );

  productForms: ProductFormResource[] = [];

  formFields: ProductFieldResource[] = [];

  coApplicantFormFields: ProductFieldResource[] = [];

  constructor(
    private leadSourceService: LeadSourceService,
    private productFormService: ProductFormsService,
    private leadEndpointService: LeadEndpointService,
    private toastService: ToastrService,
  ) { }

  ngOnInit() {
    this.createLeadFormGroup.get('leadSource')!.valueChanges.subscribe((leadSourceId) => {
       this.productFormService.getProductForms(leadSourceId).subscribe(
        (productForms) => {
          this.productForms = productForms;
          const productFormFormField = this.createLeadFormGroup.controls.productForm;
          productFormFormField!.enable();
          // select first as default
          if (productForms.length && productForms[0].platformProductId) {
            productFormFormField?.setValue(productForms[0].platformProductId!);
          }
        },
        () => {
          this.productForms = [];
          this.createLeadFormGroup.get('productForm')?.disable();
        }
      );
    });
    this.createLeadFormGroup.get('productForm')!.valueChanges.subscribe((platformProductId) => {
      if (!platformProductId) {
        return;
      }

      this.leadEndpointService.getPlatformProductFields(platformProductId).pipe(
        // Collect co-applicant fields
        tap((formFields) => {
          this.coApplicantFormFields = formFields.filter(
            (formField) => formField.parameterName.startsWith('coApplicant_*_'),
          );
        }),
        // Filter out co-applicant fields that need special handling
        map((formFields) => formFields.filter(
          (formField) => !formField.parameterName.startsWith('coApplicant_*_')),
        ),
      ).subscribe(
        (formFields) => {
          // first define array of form fields
          this.getProductFields().controls = formFields.map(
            (formField) => this.createFormField(formField),
          );
          // ...then update template
          this.formFields = formFields;
        },
      );
    });
  }

  private createFormField(formField: ProductFieldResource): FormControl<string | null> {
    return new FormControl<string | null>(formField.defaultValue, [
      ...(formField.required ? [Validators.required] : []),
      Validators.maxLength(formField.length),
    ]);
  }

  getProductFields(): FormArray<FormControl<string | null>> {
    return this.createLeadFormGroup.controls.productFields;
  }

  getCoApplicantFields(): FormArray<FormGroup<CoApplicantForm>> {
    return this.createLeadFormGroup.controls.coApplicantFields;
  }

  onCreateClick() {
    this.isCreatingLead = true;
    // handle main data
    const data = this.getProductFields().controls.reduce((controls: any, control, index) => {
      if (control.value !== '') {
        controls[this.formFields[index].parameterName] = control.value;
      }
      return controls;
    }, {});

    // handle co-applicants
    this.getCoApplicantFields().controls.forEach((coApplicant, i) => {
        coApplicant.controls.coApplicant.controls.reduce(
          (controls, control, j) => {
            if (control.value !== '') {
              const parameterName = this.coApplicantFormFields[j].parameterName.replace('*', '' + i);
              controls[parameterName] = control.value;
            }
            return controls;
          },
          data,
        );
    });

    this.leadSourceService.createLead(
      this.createLeadFormGroup.controls.leadSource.value,
      data,
    ).pipe(
      finalize(() => {
        this.isCreatingLead = false;
      }),
    ).subscribe({
      next: () => {
        this.toastService.success('Lead wurde erzeugt');
      },
      error: () => {
        this.toastService.error('Lead konnte nicht erzeugt werden');
      }
    });
  }

  onFillOutClick() {
    this.getProductFields().controls.forEach((control, index) => {
      const exampleValue = EXAMPLE_LEAD[this.formFields[index].parameterName];
      if (!control.value && exampleValue !== undefined) {
        control.setValue(exampleValue);
      }
    });
    this.getCoApplicantFields().controls.forEach((coApplicant) => {
      const fields = coApplicant.controls.coApplicant.controls;
      fields.forEach((control, index) => {
        const exampleValue = EXAMPLE_LEAD[this.coApplicantFormFields[index].parameterName];
        if (!control.value && !!exampleValue) {
          control.setValue(exampleValue);
        }
      });
    });
  }

  onResetClick() {
    this.getProductFields().controls.forEach((control, index) => {
       control.setValue(this.formFields[index].defaultValue);
    });
    this.getCoApplicantFields().controls = [];
  }

  onAddCoApplicant() {
    this.getCoApplicantFields().controls.push(new FormGroup<CoApplicantForm>({
      coApplicant: new FormArray<FormControl<string | null>>(this.coApplicantFormFields.map(
        (formField) => this.createFormField(formField)),
      ),
    }));
  }

  isValid(): boolean {
    return this.formFields.length > 0
      && !this.getProductFields().controls.some((control: AbstractControl) => !control.valid)
      && this.createLeadFormGroup.get('productForm')!.valid;
  }
}
